import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { TypeAnimation } from "react-type-animation";
import { FiArrowDownRight, FiGithub, FiLinkedin, FiMail } from "react-icons/fi";

const stats = [
  { value: "14+", label: "Projects shipped" },
  { value: "6", label: "Months at Skymirror" },
  { value: "MSc", label: "Computer Science" },
  { value: "∞", label: "Cups of coffee" },
];

const socials = [
  { Icon: FiGithub, href: "https://github.com/albinademaj", label: "GitHub" },
  {
    Icon: FiLinkedin,
    href: "https://www.linkedin.com/in/albina-demaj-881293300/",
    label: "LinkedIn",
  },
  { Icon: FiMail, href: "mailto:demajalbina3@gmail.com", label: "Email" },
];

const rise = {
  hidden: { opacity: 0, y: 26 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.1 + i * 0.09,
      duration: 0.8,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const portraitY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const copyY = useTransform(scrollYProgress, [0, 1], [0, 40]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="home"
      ref={ref}
      className="grain relative overflow-hidden bg-ink pb-20 pt-32 sm:pt-36 lg:pb-16 lg:pt-40"
    >
      {/* Atmosphere: grid, two soft light sources, a fade to the next section */}
      <div className="grid-lines absolute inset-0 opacity-60" />
      <div className="absolute -left-40 top-10 h-[540px] w-[540px] rounded-full glow-moss" />
      <div className="absolute -right-32 top-1/3 h-[620px] w-[620px] rounded-full glow-sage" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-ink to-transparent" />

      <div className="shell relative z-10 px-5 sm:px-8">
        <div className="grid items-center gap-16 lg:grid-cols-[1.15fr_0.85fr] lg:gap-12">
          {/* Copy */}
          <motion.div style={{ y: copyY }}>
            <motion.div
              variants={rise}
              initial="hidden"
              animate="show"
              custom={0}
              className="inline-flex items-center gap-3 rounded-full border border-cream/10 bg-cream/[0.04] px-4 py-2"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-pulse-ring rounded-full bg-sage" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-sage" />
              </span>
              <span className="eyebrow text-cream/70">
                Available for work — 2026
              </span>
            </motion.div>

            <motion.h1
              variants={rise}
              initial="hidden"
              animate="show"
              custom={1}
              className="display mt-8 text-[3.1rem] leading-[0.95] text-cream sm:text-[4.6rem] md:text-[5.6rem] lg:text-[6.1rem]"
            >
              I build web
              <br />
              <em className="text-gradient italic">experiences</em>
              <br />
              that matter.
            </motion.h1>

            <motion.div
              variants={rise}
              initial="hidden"
              animate="show"
              custom={2}
              className="mt-8 flex items-center gap-3 font-mono text-sm text-cream/50"
            >
              <span className="text-sage">&gt;</span>
              <span>
                building{" "}
                <TypeAnimation
                  sequence={[
                    "elegant React interfaces",
                    1800,
                    "premium Laravel systems",
                    1800,
                    "clean admin dashboards",
                    1800,
                    "full-stack web apps",
                    1800,
                  ]}
                  wrapper="span"
                  speed={45}
                  className="text-mint"
                  repeat={Infinity}
                />
              </span>
            </motion.div>

            <motion.p
              variants={rise}
              initial="hidden"
              animate="show"
              custom={3}
              className="mt-7 max-w-xl text-[1.05rem] leading-8 text-cream/55"
            >
              I’m <span className="font-medium text-cream">Albina Demaj</span> —
              a Computer Science Master’s student and full-stack developer. I
              turn rough ideas into products that are fast, considered and
              genuinely nice to use.
            </motion.p>

            <motion.div
              variants={rise}
              initial="hidden"
              animate="show"
              custom={4}
              className="mt-10 flex flex-wrap items-center gap-5"
            >
              <a
                href="#projects"
                className="group inline-flex items-center gap-3 rounded-full bg-mint px-7 py-4 text-sm font-semibold text-ink transition duration-300 hover:bg-cream"
              >
                See selected work
                <FiArrowDownRight className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:translate-y-0.5" />
              </a>

              <a
                href="#contact"
                className="link-underline text-sm font-medium text-cream/70 transition-colors hover:text-cream"
              >
                Or get in touch →
              </a>
            </motion.div>

            <motion.div
              variants={rise}
              initial="hidden"
              animate="show"
              custom={5}
              className="mt-10 flex items-center gap-3"
            >
              {socials.map(({ Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target={href.startsWith("http") ? "_blank" : undefined}
                  rel={
                    href.startsWith("http") ? "noopener noreferrer" : undefined
                  }
                  aria-label={label}
                  className="flex h-11 w-11 items-center justify-center rounded-full border border-cream/10 text-cream/60 transition duration-300 hover:-translate-y-1 hover:border-mint/40 hover:text-mint"
                >
                  <Icon />
                </a>
              ))}
            </motion.div>
          </motion.div>

          {/* Portrait */}
          <motion.div
            style={{ y: portraitY }}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-auto w-full max-w-[420px]"
          >
            <div className="absolute -inset-6 rounded-[3rem] glow-sage" />
            <div className="absolute -bottom-5 -right-5 h-full w-full rounded-[2.2rem] border border-mint/25" />

            <div className="relative overflow-hidden rounded-[2.2rem] border border-cream/10 bg-forest">
              <img
                src="/albina.jpg"
                alt="Portrait of Albina Demaj"
                width={929}
                height={1054}
                fetchPriority="high"
                decoding="async"
                className="h-[480px] w-full object-cover object-top grayscale-[0.15] transition duration-700 hover:grayscale-0 sm:h-[540px]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent" />

              <div className="absolute inset-x-5 bottom-5">
                <p className="display text-2xl text-cream">Albina Demaj</p>
                <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.22em] text-mint">
                  Full-Stack Developer
                </p>
              </div>
            </div>

            <div className="absolute -left-6 top-10 hidden animate-floaty rounded-2xl border border-cream/10 bg-forest px-4 py-3 sm:block">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40">
                Stack
              </p>
              <p className="mt-1 text-sm font-medium text-mint">
                React · Laravel
              </p>
            </div>
          </motion.div>
        </div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.75, duration: 0.8 }}
          className="mt-20 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-cream/10 bg-cream/10 sm:grid-cols-4"
        >
          {stats.map((stat) => (
            <div key={stat.label} className="bg-ink px-6 py-7 text-center">
              <p className="display text-3xl text-mint sm:text-4xl">
                {stat.value}
              </p>
              <p className="mt-2 text-[12px] uppercase tracking-[0.16em] text-cream/40">
                {stat.label}
              </p>
            </div>
          ))}
        </motion.div>
      </div>

      <motion.div
        style={{ opacity: fade }}
        className="pointer-events-none absolute bottom-6 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 lg:flex"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-cream/30">
          Scroll
        </span>
        <span className="h-10 w-px bg-gradient-to-b from-mint/60 to-transparent" />
      </motion.div>
    </section>
  );
};

export default Hero;
